import { useEffect, useMemo, useRef } from "react";
import trpc from "@/utils/trpc";
import useCompletedByItemId from "@/hooks/use-completed-by-item-id";
import useCurrentModuleItemId from "@/hooks/use-current-module-item-id";

/**
 * useAutoCompleteOnView
 * Side-effect hook: when the current module item has completion_requirement "must_view",
 * and it's not completed yet, mark it as done and invalidate cached progression.
 */
export default function useAutoCompleteOnView() {
  const currentModuleItemId = useCurrentModuleItemId();

  const { data: modules } = trpc.course.getModules.useQuery(undefined, { staleTime: Infinity });
  const upsertProgressionMutation = trpc.progression.upsert.useMutation();
  const utils = trpc.useUtils();
  const { isCompleted: isCompletedFn } = useCompletedByItemId();

  const currentItem = useMemo(() => {
    if (!modules || !currentModuleItemId) return null;
    for (const mod of modules) {
      for (const sub of mod.subModules) {
        const found = sub.items.find(it => String(it.id) === currentModuleItemId);
        if (found) return found;
      }
    }
    return null;
  }, [modules, currentModuleItemId]);

  const requiresMustView =
    !!currentItem?.completion_requirement && currentItem?.completion_requirement?.type === "must_view";
  const isCompleted = isCompletedFn(currentModuleItemId);

  // Track attempted resource ids to avoid retry loops; only try once per id
  const attemptedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id = currentModuleItemId ? String(currentModuleItemId) : null;
    if (!id) return;
    if (!requiresMustView) return;
    if (isCompleted) return;
    if (attemptedIdsRef.current.has(id)) return;

    (async () => {
      try {
        // Mark as attempted before calling the mutation to ensure single try even on failure
        attemptedIdsRef.current.add(id);
        await upsertProgressionMutation.mutateAsync({ itemId: id, completed: true });
        await utils.progression.get.invalidate();
      } catch (_) {
        // ignore
      }
    })();
  }, [currentModuleItemId, requiresMustView, isCompleted, upsertProgressionMutation, utils.progression.get]);
}
